import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import {
  APPOINTMENT_HOLDING_OPTIONS,
  DEGREE_FALLBACK,
  FILE_TYPE_FALLBACK,
  LEGAL_QUESTIONS,
  MARITAL_STATUS_OPTIONS,
  SECTION_13A_NOTE,
  SECTION_13B_NOTE,
  SECTION_13_PARTICULARS,
  canEditRequest,
  downloadBase64File,
  fetchFatherNameFromMyProfile,
  findOneEAttestationRequest,
  formatPresentAge,
  genderLabel,
  getEAttestationReqId,
  getApproverDisplay,
  getFatherNameFromFamilyRows,
  getStatusCode,
  getStatusLabel,
  getToReferences,
  nationalityLabel,
  pick,
  toDisplayDate,
  toYesNo,
  viewBase64File,
} from "./eAttestation.service";
import { IdentificationCertificate } from "./IdentificationCertificate";

const EMPTY = "—";
const ACCENT = "#ab1f24";

type Option = {
  value?: string;
  type_id?: string;
  label?: string;
  type_text?: string;
};

type Tone = {
  color: string;
  bg: string;
  border: string;
  icon: string;
};

function hasText(value: any): boolean {
  const text = String(value ?? "").trim();
  return Boolean(text) && text !== "-" && text !== "—" && text.toLowerCase() !== "null";
}

function textOrEmpty(value: any): string {
  return hasText(value) ? String(value).trim() : EMPTY;
}

function optionLabel(options: Option[], value: any): string {
  const raw = String(value || "").trim();
  if (!raw) return EMPTY;
  const found = options.find(
    (item) => String(item.value || item.type_id) === raw || String(item.label || item.type_text) === raw
  );
  return found?.label || found?.type_text || raw;
}

function ageLabel(value: any): string {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits) return EMPTY;
  return `${Number(digits)} years`;
}

function personName(detail: any): string {
  const full = String(pick(detail, "EmployeeName", "employeeName") || "").trim();
  if (full) return full;
  return `${pick(detail, "Firstname", "firstname")} ${pick(detail, "Lastname", "lastname")}`.trim() || EMPTY;
}

function initials(name: string): string {
  const parts = String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);
  return parts.map((part) => part.charAt(0)).join("").toUpperCase() || "EA";
}

function isConfirmed(value: any): boolean {
  return value === true || String(value).toLowerCase() === "true";
}

function statusTone(detail: any): Tone {
  const code = getStatusCode(detail);
  if (code === "A") {
    return { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0", icon: "ti ti-circle-check" };
  }
  if (code === "R") {
    return { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: "ti ti-circle-x" };
  }
  if (code === "D") {
    return { color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd", icon: "ti ti-file-text" };
  }
  return { color: "#c2410c", bg: "#fff7ed", border: "#fed7aa", icon: "ti ti-clock" };
}

function rowsOf(list: any): any[] {
  return Array.isArray(list) ? list : [];
}

export function EAttestationDetailsBody({ detail }: { detail: any }) {
  const [profileFatherName, setProfileFatherName] = useState("");
  const view = useMemo(() => {
    const residences = rowsOf(detail?.Residence || detail?.residence);
    const education = rowsOf(detail?.Education || detail?.education);
    const appointments = rowsOf(detail?.Appointment || detail?.appointment);
    const attachments = rowsOf(detail?.Attachment || detail?.attachment);
    const family = rowsOf(detail?.Family || detail?.family);
    const foreignInfo = rowsOf(detail?.ForeignInfo || detail?.foreignInfo);
    const remarks = rowsOf(detail?.Remarks_data || detail?.remarks_data);
    const migrationRows = rowsOf(detail?.Migration || detail?.migration);
    const references = getToReferences(detail);
    return {
      residences,
      education,
      appointments,
      attachments,
      family,
      foreignInfo,
      remarks,
      migrationRows,
      references,
      migrated: toYesNo(pick(detail, "MigratedFromPakOrBangladesh")) === "YES",
      childAbroad: toYesNo(pick(detail, "HasChildStudyingAbroad")) === "YES",
      scSt: toYesNo(pick(detail, "ScStObc")) === "YES",
      heldGovt: toYesNo(pick(detail, "HoldsOrHeldGovtAppointment")) === "YES",
      pwdYes: toYesNo(pick(detail, "Pwd")) === "YES",
      ewsYes: toYesNo(pick(detail, "Ews")) === "YES",
      exYes: toYesNo(pick(detail, "ExServiceMan")) === "YES",
      declared: isConfirmed(pick(detail, "DeclarationConfirmed")),
      aadharNo: pick(detail, "AadharNo", "aadharNo"),
      panNo: pick(detail, "PanNo", "panNo"),
      name: personName(detail),
      fatherName: getFatherNameFromFamilyRows(family),
      empId: pick(detail, "EmpID", "empId"),
      designation: pick(detail, "Designation"),
      requestId: pick(detail, "Reqid", "reqid"),
      createdOn: toDisplayDate(pick(detail, "Created_at", "created_at")),
      approver: getApproverDisplay(detail),
    };
  }, [detail]);

  useEffect(() => {
    const empId = String(view.empId || "").trim();
    if (view.fatherName) return;
    let cancelled = false;
    const load = async () => {
      try {
        const name = await fetchFatherNameFromMyProfile(empId);
        if (!cancelled && name) setProfileFatherName(name);
      } catch {
        /* keep empty */
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [view.empId, view.fatherName]);

  const tone = statusTone(detail);

  return (
    <div className="ea-details">
      <div className="ea-hero" style={{ background: tone.bg, borderColor: tone.border }}>
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div className="d-flex align-items-center gap-3 min-w-0">
            <div className="ea-avatar">{initials(view.name)}</div>
            <div className="min-w-0">
              <h4 className="ea-name mb-1">{view.name}</h4>
              <div className="ea-meta">
                {[view.designation, view.empId].filter(hasText).join("  ·  ") || EMPTY}
              </div>
              <div className="ea-meta mt-1">
                Request {view.requestId || EMPTY}
                {view.createdOn !== EMPTY ? `  ·  Created ${view.createdOn}` : ""}
              </div>
            </div>
          </div>
          <div className="text-md-end">
            <span
              className="ea-status-pill"
              style={{ color: tone.color, background: "#fff", borderColor: tone.border }}
            >
              <i className={tone.icon} />
              {getStatusLabel(detail)}
            </span>
            <div className="ea-meta mt-2">Current Approver</div>
            <div className="ea-approver">{view.approver}</div>
          </div>
        </div>
      </div>

      <Section title="Employee & Request Info" icon="ti ti-user">
        <Subhead>Employment</Subhead>
        <div className="row g-2">
          <Field label="Employee Name" value={view.name} />
          <Field label="Employee ID" value={view.empId} />
          <Field label="Current Approver" value={view.approver} />
          <Field label="Approver Level" value={pick(detail, "ApproverLevel", "approverLevel")} hideEmpty />
          <Field label="Designation" value={view.designation} />
          <Field label="Department" value={pick(detail, "Department")} hideEmpty />
          <Field label="Place of Posting" value={pick(detail, "PlaceOfPosting")} hideEmpty />
          <Field label="Date of Joining" value={toDisplayDate(pick(detail, "DateOfJoining"))} />
          <Field label="Pay Scale" value={pick(detail, "PayScale")} />
          <Field label="Gender" value={genderLabel(pick(detail, "Gender"))} />
          <Field label="Recruit Category" value={pick(detail, "RecruitUnderCatagory")} />
          <Field label="Marital Status" value={optionLabel(MARITAL_STATUS_OPTIONS, pick(detail, "MarritalStatus"))} />
          <Field label="Blood Group" value={pick(detail, "BloodGroup")} />
        </div>

        <Subhead className="mt-4">Contact</Subhead>
        <div className="row g-2">
          <Field label="Mobile" value={pick(detail, "MobileNo")} />
          <Field label="Alternate No" value={pick(detail, "AlternateMobileNo")} hideEmpty />
          <Field label="Email" value={pick(detail, "EmailID")} />
        </div>

        <Subhead className="mt-4">Category</Subhead>
        <div className="d-flex flex-wrap gap-2">
          <Flag label="PWD" yes={view.pwdYes} extra={view.pwdYes ? pick(detail, "PwdType") : ""} />
          <Flag label="Ex-Serviceman" yes={view.exYes} />
          <Flag
            label="EWS"
            yes={view.ewsYes}
            extra={
              view.ewsYes
                ? [pick(detail, "EwsCertificateNo"), toDisplayDate(pick(detail, "EwsCertificateValidDate"))]
                    .filter((item) => hasText(item) && item !== EMPTY)
                    .join(" · ")
                : ""
            }
          />
        </div>
      </Section>

      <Section title="Address Details" icon="ti ti-map-pin">
        <div className="row g-3">
          <div className="col-lg-6">
            <AddressCard
              title="Present Address"
              full={pick(detail, "PresentAddressFull")}
              rows={[
                ["House No", pick(detail, "PresentAddrHouseNo")],
                ["Lane / Street / Town", pick(detail, "PresentAddrLaneStreet")],
                ["Police Station", pick(detail, "PresentAddrPoliceStation")],
                ["District", pick(detail, "PresentAddrDistrict")],
                ["District HQ & PIN", pick(detail, "PresentAddrDistHeadQuartAndPin")],
                ["State", pick(detail, "PresentAddressState")],
              ]}
            />
          </div>
          <div className="col-lg-6">
            <AddressCard
              title="Permanent / Home Address"
              full={pick(detail, "PermanantAddressFull")}
              rows={[
                ["House No", pick(detail, "PermanantAddrHouseNo")],
                ["Lane / Street / Town", pick(detail, "PermanantAddrLaneStreet")],
                ["Police Station", pick(detail, "PermanantAddrPoliceStationnant")],
                ["District", pick(detail, "PermanantAddrDistrict")],
                ["District HQ & PIN", pick(detail, "PermanantDistHeadQuartAndPin")],
                ["State", pick(detail, "PermanentAddressState")],
              ]}
            />
          </div>
        </div>
      </Section>

      <Section title="Migrated from Pakistan / Bangladesh" icon="ti ti-world">
        <Flag label="Migrated" yes={view.migrated} />
        {view.migrated ? (
          <div className="row g-3 mt-2">
            {(view.migrationRows.length ? view.migrationRows : [detail]).map((row: any, idx: number) => (
              <div className="col-lg-6" key={idx}>
                <AddressCard
                  title={view.migrationRows.length ? `Migration record ${idx + 1}` : "Address in that country"}
                  full={pick(row, "MigrationAddrAndDate")}
                  rows={[
                    ["House No", pick(row, "MigrationAddrHouseNo")],
                    ["Lane / Street / Town", pick(row, "MigrationAddrLaneStreet")],
                    ["Police Station", pick(row, "MigrationAddrPoliceStation")],
                    ["District", pick(row, "MigrationAddrDistrictNo")],
                    ["PIN", pick(row, "MigrationAddrPincode")],
                    ["State", pick(row, "MigrationAddrState")],
                  ]}
                />
              </div>
            ))}
          </div>
        ) : null}
      </Section>

      <Section title="Residence History" icon="ti ti-home">
        <SimpleTable
          columns={["From", "To", "Residential Address in Full", "District Headquarter"]}
          rows={view.residences.map((row: any) => [
            toDisplayDate(pick(row, "FromDate")),
            toDisplayDate(pick(row, "ToDate")),
            textOrEmpty(pick(row, "ResidenceFullAddress")),
            textOrEmpty(pick(row, "DistrictHeadQuarterAddr")),
          ])}
        />
      </Section>

      <Section title="Family Details" icon="ti ti-users">
        {view.family.length ? (
          <div className="row g-3">
            {view.family.map((row: any, idx: number) => (
              <div className="col-md-6" key={`${pick(row, "Name")}-${idx}`}>
                <div className="ea-subcard h-100">
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-3">
                    <div>
                      <div className="ea-field-lbl">Family member {idx + 1}</div>
                      <div className="ea-card-title">{textOrEmpty(pick(row, "Name"))}</div>
                    </div>
                    <span className="ea-soft-chip">{textOrEmpty(pick(row, "RelationType"))}</span>
                  </div>
                  <div className="row g-2">
                    <Field sm label="Phone" value={pick(row, "PhoneNo")} />
                    <Field sm label="Nationality" value={nationalityLabel(pick(row, "Nationality"))} />
                    <Field sm label="Date of Birth" value={toDisplayDate(pick(row, "DateOfBirth"))} />
                    <Field sm label="Occupation" value={pick(row, "Occupation")} />
                    <Field sm label="Monthly Income" value={pick(row, "MonthlyIncome")} />
                    <Field sm label="Place of Birth" value={pick(row, "PlaceOfBirth")} />
                    <Field wide label="Present Postal Address" value={pick(row, "PresentAddress")} />
                    <Field wide label="Permanent Home Address" value={pick(row, "PermanentAddress")} />
                    <div className="col-12 mt-1">
                      {pick(row, "AttachmentFileName") || pick(row, "AttachmentFileContent") ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() =>
                            viewBase64File(
                              pick(row, "AttachmentFileContent"),
                              pick(row, "AttachmentFileName"),
                              pick(row, "FileMimeType")
                            )
                          }
                        >
                          View File
                        </button>
                      ) : (
                        <span className="ea-meta">No file attached</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyNote />
        )}
      </Section>

      <Section title="Children Studying Abroad" icon="ti ti-school">
        <Flag label="Has child studying abroad" yes={view.childAbroad} />
        {view.childAbroad ? (
          <div className="mt-3">
            <SimpleTable
              columns={[
                "Child Name",
                "Nationality by Birth",
                "Country Studying In",
                "Studying Since",
                "Place of Birth State",
              ]}
              rows={view.foreignInfo.map((row: any) => [
                textOrEmpty(pick(row, "ChildName")),
                textOrEmpty(pick(row, "NationalityByBirth")),
                textOrEmpty(pick(row, "CountryStudyingIn")),
                toDisplayDate(pick(row, "StudyingSinceDate")),
                textOrEmpty(pick(row, "PlaceOfBirthState")),
              ])}
            />
          </div>
        ) : null}
      </Section>

      <Section title="Personal Details" icon="ti ti-id">
        <div className="row g-2">
          <Field label="Nationality" value={nationalityLabel(pick(detail, "Natio"))} />
          <Field label="Date of Birth" value={toDisplayDate(pick(detail, "DateOfBirth"))} />
          <Field label="Present Age" value={formatPresentAge(pick(detail, "PresentAge")) || EMPTY} />
          <Field label="Age at Matriculation" value={ageLabel(pick(detail, "AgeAtMatriculation"))} />
          <Field label="Place of Birth" value={pick(detail, "BirthDistrictAndState")} />
          <Field label="Father originally belong" value={pick(detail, "FatherBirthDistrictAndState")} />
          <Field label="Religion" value={pick(detail, "Religion")} />
          <Field label="Member of SC/ST/OBC" value={<YesNo value={pick(detail, "ScStObc")} />} />
          {view.scSt ? (
            <>
              <Field label="Subcast" value={pick(detail, "SubCast")} />
              <Field label="Issuing Authority" value={pick(detail, "IssuingAuthority")} />
              <Field label="Address Authority" value={pick(detail, "AddressAuthority")} />
            </>
          ) : null}
        </div>
      </Section>

      <Section title="Education Qualification" icon="ti ti-certificate">
        {view.education.length ? (
          <div className="row g-3">
            {view.education.map((row: any, idx: number) => (
              <div className="col-md-6" key={`${pick(row, "RollNo")}-${idx}`}>
                <div className="ea-subcard h-100">
                  <div className="ea-card-title mb-3" style={{ color: ACCENT }}>
                    {optionLabel(DEGREE_FALLBACK, pick(row, "EducationLevel", "CourseName"))}
                  </div>
                  <div className="row g-2">
                    <Field sm label="School / College" value={pick(row, "InstituteName")} />
                    <Field sm label="Board / University" value={pick(row, "BoardOrUniversity")} />
                    <Field sm label="Roll No" value={pick(row, "RollNo")} />
                    <Field sm label="Division" value={pick(row, "Division")} />
                    <Field sm label="Course Name" value={pick(row, "CourseName")} />
                    <Field sm label="Percentage of Marks" value={pick(row, "PercentageOfMarks")} />
                    <Field sm label="Start Date" value={toDisplayDate(pick(row, "StartDate"))} />
                    <Field sm label="End Date" value={toDisplayDate(pick(row, "EndDate"))} />
                    <div className="col-12 mt-1">
                      {pick(row, "CertificateFileName") ? (
                        <FileLink
                          name={pick(row, "CertificateFileName")}
                          content={pick(row, "CertificateFileContent")}
                          mime={pick(row, "FileMimeType")}
                        />
                      ) : (
                        <span className="ea-meta">No certificate uploaded</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyNote />
        )}
      </Section>

      <Section title="Government / Private Appointment" icon="ti ti-briefcase">
        <Flag label="Holding / held appointment" yes={view.heldGovt} />
        {view.heldGovt ? (
          view.appointments.length ? (
            <div className="row g-3 mt-2">
              {view.appointments.map((row: any, idx: number) => (
                <div className="col-md-6" key={idx}>
                  <div className="ea-subcard h-100">
                    <div className="ea-card-title mb-3">Appointment {idx + 1}</div>
                    <div className="row g-2">
                      <Field sm label="Period From" value={toDisplayDate(pick(row, "StartDate"))} />
                      <Field sm label="Period To" value={toDisplayDate(pick(row, "EndDate"))} />
                      <Field sm label="Designation / Emoluments" value={pick(row, "Designation")} />
                      <Field
                        sm
                        label="Holding"
                        value={optionLabel(APPOINTMENT_HOLDING_OPTIONS, pick(row, "AppointmentType"))}
                      />
                      <Field wide label="Employer Name & Address" value={pick(row, "OrganizationNameAndAddress")} />
                      <Field wide label="Reason for Leaving" value={pick(row, "ReasonForLeaving")} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyNote />
          )
        ) : null}
      </Section>

      <Section title="Uploaded Files" icon="ti ti-paperclip">
        {view.attachments.length ? (
          <div className="d-flex flex-column gap-2">
            {view.attachments.map((row: any, idx: number) => (
              <div className="ea-file-row" key={`${pick(row, "FileName")}-${idx}`}>
                <div className="ea-file-icon">
                  <i className="ti ti-file-text" />
                </div>
                <div className="min-w-0 flex-grow-1">
                  <div className="ea-card-title">
                    {optionLabel(FILE_TYPE_FALLBACK, pick(row, "FileType"))}
                  </div>
                  <div className="ea-meta text-truncate">{pick(row, "FileName") || "No file chosen"}</div>
                </div>
                {pick(row, "FileContent") ? (
                  <FileLink
                    name={pick(row, "FileName")}
                    content={pick(row, "FileContent")}
                    mime={pick(row, "FileMimeType")}
                    button
                  />
                ) : (
                  <span className="ea-meta">Not uploaded</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <EmptyNote text="No documents uploaded" />
        )}
      </Section>

      <Section title="Legal & Disciplinary" icon="ti ti-scale">
        <div className="mb-3">
          <Flag label="Govt disciplinary proceedings" yes={toYesNo(pick(detail, "GovtDisciplinaryProceedings")) === "YES"} />
        </div>
        <div className="ea-legal">
          {LEGAL_QUESTIONS.map((question) => (
            <div className="ea-legal-row" key={question.key}>
              <span>{question.label.replace(/^\d+\([A-Z]\)\s*/, "")}</span>
              <YesNo value={pick(detail, question.key, question.legacy) || "NO"} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="13. If the answer is Yes" icon="ti ti-notes">
        <p className="ea-body-text">{SECTION_13_PARTICULARS}</p>
        <div className="ea-note mb-3">
          <p className="mb-1">{SECTION_13A_NOTE}</p>
          <p className="mb-0">{SECTION_13B_NOTE}</p>
        </div>
        <p className="ea-body-text mb-0">{textOrEmpty(pick(detail, "SpecificAnswers"))}</p>
      </Section>

      <Section title="References" icon="ti ti-address-book">
        <div className="row g-3">
          {(view.references.length ? view.references : [{}, {}]).slice(0, 2).map((ref: any, idx: number) => (
            <div className="col-md-6" key={ref?.referenceNo || idx}>
              <div className="ea-subcard h-100">
                <div className="ea-card-title mb-3">Reference {idx + 1}</div>
                <div className="row g-2">
                  <Field wide label="Name" value={ref?.referenceName} />
                  <Field wide label="Address" value={ref?.referenceAddress} />
                  <Field wide label="Mobile" value={ref?.referenceMobileNo} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Declaration" icon="ti ti-checkbox">
        <div className="d-flex align-items-start gap-3">
          <div className={`ea-icon-wrap ${view.declared ? "is-yes" : "is-no"}`}>
            <i className={`ti ${view.declared ? "ti-circle-check" : "ti-circle-x"}`} />
          </div>
          <div>
            <div className="ea-card-title">
              {view.declared ? "Declaration confirmed" : "Declaration not confirmed"}
            </div>
            <p className="ea-meta mb-0 mt-1">
              Certified that the information is correct and complete to the best of knowledge and belief.
            </p>
          </div>
        </div>
      </Section>

      <IdentificationCertificate
        fullName={view.name === EMPTY ? "" : view.name}
        fatherName={(view.fatherName || profileFatherName).toUpperCase()}
        aadharNo={view.aadharNo}
        panNo={view.panNo}
      />

      {view.remarks.length ? (
        <Section title="Approval Remarks" icon="ti ti-message">
          <div className="d-flex flex-column gap-2">
            {view.remarks.map((row: any, idx: number) => {
              const approver = getApproverDisplay({
                ...row,
                ApproverUserID:
                  pick(row, "ApproverUserID", "approverUserID", "ApproverId", "approverId", "EmpID", "empId") || "",
              });
              return (
                <div className="ea-remark" key={idx}>
                  <div className="ea-avatar sm">{initials(approver)}</div>
                  <div className="min-w-0 flex-grow-1">
                    <div className="d-flex justify-content-between gap-2 flex-wrap">
                      <div className="ea-card-title">{approver}</div>
                      <span className="ea-meta">{toDisplayDate(pick(row, "created_at", "Created_at"))}</span>
                    </div>
                    <p className="ea-body-text mb-0 mt-1">
                      {textOrEmpty(pick(row, "remarks", "Remarks", "hRemarks"))}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      ) : null}

      <style>{EA_DETAILS_CSS}</style>
    </div>
  );
}

const EAttestationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const routes = all_routes;
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await findOneEAttestationRequest(decodeURIComponent(id));
        setDetail(data);
      } catch (err: any) {
        setDetail(null);
        toast.error(Api.getErrorMessage(err) || "Failed to load request details");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">E-Attestation Request Details</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.eAttestation.list}>E-Attestation Process</Link>
                </li>
                <li className="breadcrumb-item active">View Details</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex gap-2">
            {detail && canEditRequest(detail) ? (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() =>
                  navigate(
                    routes.eAttestation.edit.replace(
                      ":id",
                      encodeURIComponent(
                        getEAttestationReqId(detail) || String(pick(detail, "Id", "id") || "")
                      )
                    )
                  )
                }
              >
                Edit
              </button>
            ) : null}
            <Link to={routes.eAttestation.list} className="btn btn-light">
              Back
            </Link>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : !detail ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">No records found</div>
          </div>
        ) : (
          <EAttestationDetailsBody detail={detail} />
        )}
      </div>
    </div>
  );
};

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="ea-section">
      <div className="ea-section-hd">
        {icon ? (
          <span className="ea-icon-wrap">
            <i className={icon} />
          </span>
        ) : null}
        <h5>{title}</h5>
      </div>
      <div className="ea-section-bd">{children}</div>
    </section>
  );
}

function Subhead({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <div className={`ea-subhead ${className}`.trim()}>{children}</div>;
}

function Field({
  label,
  value,
  wide,
  sm,
  hideEmpty,
}: {
  label: string;
  value: any;
  wide?: boolean;
  sm?: boolean;
  hideEmpty?: boolean;
}) {
  const display = React.isValidElement(value) ? value : textOrEmpty(value);
  const empty = !React.isValidElement(value) && display === EMPTY;
  if (hideEmpty && empty) return null;
  const col = wide ? "col-12" : sm ? "col-sm-6" : "col-sm-6 col-xl-4";
  return (
    <div className={col}>
      <div className="ea-tile">
        <span className="ea-field-lbl">{label}</span>
        <span className={`ea-field-val${empty ? " is-empty" : ""}`}>{display}</span>
      </div>
    </div>
  );
}

function YesNo({ value }: { value: any }) {
  const yes = toYesNo(value) === "YES";
  return <span className={`ea-yn ${yes ? "yes" : "no"}`}>{yes ? "Yes" : "No"}</span>;
}

function Flag({ label, yes, extra }: { label: string; yes: boolean; extra?: string }) {
  return (
    <span className={`ea-flag ${yes ? "yes" : "no"}`}>
      <i className={`ti ${yes ? "ti-check" : "ti-minus"}`} />
      <span>
        {label}
        {yes && extra ? ` · ${extra}` : ""}
      </span>
    </span>
  );
}

function AddressCard({
  title,
  full,
  rows,
}: {
  title: string;
  full?: any;
  rows: [string, any][];
}) {
  return (
    <div className="ea-subcard h-100">
      <div className="ea-card-title mb-2">{title}</div>
      <p className="ea-body-text mb-3">{textOrEmpty(full)}</p>
      <div className="row g-2">
        {rows.map(([label, value]) => (
          <Field sm key={label} label={label} value={value} hideEmpty />
        ))}
      </div>
    </div>
  );
}

function FileLink({
  name,
  content,
  mime,
  button,
}: {
  name: string;
  content: string;
  mime?: string;
  button?: boolean;
}) {
  if (!name) return <span className="ea-meta">{EMPTY}</span>;
  if (button) {
    return (
      <button
        type="button"
        className="btn btn-sm btn-outline-danger"
        onClick={() => downloadBase64File(content, name, mime)}
      >
        <i className="ti ti-download me-1" />
        Download
      </button>
    );
  }
  return (
    <button
      type="button"
      className="btn btn-link p-0 text-decoration-none"
      onClick={() => downloadBase64File(content, name, mime)}
    >
      <i className="ti ti-paperclip me-1" />
      {name}
    </button>
  );
}

function SimpleTable({ columns, rows }: { columns: string[]; rows: any[][] }) {
  if (!rows.length) return <EmptyNote />;
  return (
    <div className="table-responsive">
      <table className="table ea-table mb-0">
        <thead>
          <tr>
            <th>Sr.</th>
            {columns.map((col) => (
              <th key={col}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              <td>{idx + 1}</td>
              {row.map((cell, cellIdx) => (
                <td key={cellIdx}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyNote({ text = "No records found" }: { text?: string }) {
  return <p className="ea-meta mb-0">{text}</p>;
}

const EA_DETAILS_CSS = `
  .ea-details {
    font-family: "Roboto", "Segoe UI", sans-serif;
    color: #0f172a;
  }
  .ea-hero {
    border: 1px solid;
    border-left: 4px solid ${ACCENT};
    border-radius: 14px;
    padding: 20px 22px;
    margin-bottom: 16px;
  }
  .ea-note {
    border: 1px solid #bae6fd;
    background: #f0f9ff;
    padding: 12px 14px;
    border-radius: 10px;
    color: #0369a1;
    font-size: 14px;
  }
  .ea-avatar {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: linear-gradient(135deg, #ab1f24 0%, #d64545 100%);
    color: #fff;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.4px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ea-avatar.sm {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    font-size: 13px;
  }
  .ea-name {
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: #0f172a;
    line-height: 1.2;
  }
  .ea-meta {
    font-size: 13px;
    color: #64748b;
    font-weight: 400;
    letter-spacing: 0.01em;
  }
  .ea-approver {
    font-size: 14px;
    font-weight: 600;
    color: #0f172a;
    margin-top: 2px;
  }
  .ea-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid;
    border-radius: 999px;
    padding: 6px 12px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.01em;
  }
  .ea-section {
    background: #fff;
    border: 1px solid #e8edf3;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
    overflow: hidden;
    margin-bottom: 16px;
  }
  .ea-section-hd {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 20px;
    border-bottom: 1px solid #eef2f6;
    background: #fff;
  }
  .ea-section-hd h5 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: #0f172a;
  }
  .ea-section-bd { padding: 18px 20px 20px; }
  .ea-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #fdecec;
    color: ${ACCENT};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ea-icon-wrap.is-yes { background: #dcfce7; color: #15803d; }
  .ea-icon-wrap.is-no { background: #fee2e2; color: #dc2626; }
  .ea-subhead {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 10px;
  }
  .ea-tile {
    background: #f8fafc;
    border: 1px solid #eef2f6;
    border-radius: 10px;
    padding: 11px 13px;
    height: 100%;
  }
  .ea-field-lbl {
    display: block;
    font-size: 11px;
    font-weight: 500;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-bottom: 4px;
  }
  .ea-field-val {
    display: block;
    font-size: 14.5px;
    font-weight: 500;
    color: #0f172a;
    line-height: 1.4;
    word-break: break-word;
  }
  .ea-field-val.is-empty {
    color: #cbd5e1;
    font-weight: 400;
  }
  .ea-card-title {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: #0f172a;
  }
  .ea-body-text {
    font-size: 14px;
    line-height: 1.55;
    color: #334155;
    white-space: pre-wrap;
  }
  .ea-subcard {
    background: #f8fafc;
    border: 1px solid #e8edf3;
    border-radius: 12px;
    padding: 16px;
  }
  .ea-soft-chip {
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #475569;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 600;
  }
  .ea-yn, .ea-flag {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 999px;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 600;
  }
  .ea-yn.yes, .ea-flag.yes { background: #dcfce7; color: #166534; }
  .ea-yn.no, .ea-flag.no { background: #f1f5f9; color: #64748b; }
  .ea-legal-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid #eef2f6;
    font-size: 14px;
    line-height: 1.45;
    color: #334155;
  }
  .ea-legal-row:last-child { border-bottom: 0; }
  .ea-file-row, .ea-remark {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #f8fafc;
    border: 1px solid #e8edf3;
    border-radius: 12px;
    padding: 12px 14px;
  }
  .ea-file-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #fdecec;
    color: ${ACCENT};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ea-table {
    font-size: 13.5px;
    color: #0f172a;
  }
  .ea-table thead th {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
    background: #f8fafc;
    border-bottom: 1px solid #e8edf3;
    white-space: nowrap;
  }
  .ea-table td, .ea-table th {
    padding: 10px 12px;
    border-color: #eef2f6;
    vertical-align: middle;
  }
`;

export default EAttestationDetails;
